import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse, User } from '@/types';

type TokenPair = { accessToken: string; refreshToken: string };
type AuthResponse = { user: User; tokens: TokenPair };

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle 401 Unauthorized
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              const accessToken = response.data?.tokens?.accessToken;
              if (!accessToken) {
                throw new Error('Token refresh response missing accessToken');
              }

              localStorage.setItem('accessToken', accessToken);
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;

              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Generic request methods
  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    url: string,
    data?: any
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.api.request({
        method,
        url,
        data,
      });

      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Request failed');
      } else if (error.request) {
        throw new Error('Network error - please check your connection');
      } else {
        throw new Error(error.message || 'Request failed');
      }
    }
  }

  // HTTP methods
  async get<T>(url: string): Promise<ApiResponse<T>> {
    return this.request<T>('GET', url);
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>('POST', url, data);
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', url, data);
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', url);
  }

  // Authentication endpoints
  async register(userData: {
    email: string;
    username: string;
    password: string;
  }) {
    return this.post<AuthResponse>('/auth/register', userData);
  }

  async login(email: string, password: string) {
    return this.post<AuthResponse>('/auth/login', { email, password });
  }

  async refreshToken(refreshToken: string) {
    return this.post<{ tokens: TokenPair }>('/auth/refresh', { refreshToken });
  }

  async logout() {
    return this.post('/auth/logout');
  }

  async getProfile() {
    return this.get<{ user: User }>('/auth/profile');
  }

  async updateProfile(updates: {
    username?: string;
    settings?: any;
  }) {
    return this.put<{ user: User }>('/auth/profile', updates);
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  }

  // Game endpoints
  async getGameState(sessionId: string) {
    return this.get(`/game/state/${sessionId}`);
  }

  async startGame(level?: number) {
    return this.post('/game/start', { level });
  }

  async performGameAction(sessionId: string, action: string, data: any) {
    return this.post(`/game/action/${sessionId}`, { action, data });
  }

  async getLevels() {
    return this.get('/game/levels');
  }

  // Progress endpoints
  async getUserProgress() {
    return this.get('/progress/user');
  }

  // Research endpoints
  async getResearchTree() {
    return this.get('/research/tree');
  }

  async startResearch(researchId: string) {
    return this.post('/research/start', { researchId });
  }

  // Leaderboard endpoints
  async getLeaderboard(type: 'global' | 'weekly' = 'global') {
    return this.get(`/leaderboard/${type}`);
  }

  // Admin endpoints
  async getAdminDashboard() {
    return this.get('/admin/dashboard');
  }

  async getAdminTowers() {
    return this.get('/admin/towers');
  }

  async getAdminEnemies() {
    return this.get('/admin/enemies');
  }

  async getAdminAnalytics() {
    return this.get('/admin/analytics');
  }

  // Utility methods
  setAuthToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  setRefreshToken(token: string) {
    localStorage.setItem('refreshToken', token);
  }

  clearTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  getAuthToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }
}

// Create and export singleton instance
export const apiService = new ApiService();
export default apiService;